import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {Button} from "primereact/button";
import {setMessage} from "../app-slice";
import {notificationApi} from "../../models/notification/notification-api";
import {enablePush, pushState} from "./push-client";

// Asks once per device (dismissible for 14 days) to turn on browser push, so every device the user works on gets
// notifications — not only the one where they visited the settings page.

const DISMISS_KEY = "zofino.push.prompt.dismissed";
const DISMISS_DAYS = 14;

const ERRORS = {
    "push.blocked": "اجازه اعلان در این مرورگر مسدود است. از آیکون قفل کنار آدرس سایت، Notifications را روی Allow بگذارید.",
    "push.dismissed": "اجازه اعلان داده نشد.",
    "push.service-unreachable": "مرورگر نتوانست به سرویس Push خودش وصل شود. «اعلان‌های من ← عیب‌یابی» را ببینید.",
};

function dismissedRecently() {
    try {
        const at = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
        return at && Date.now() - at < DISMISS_DAYS * 24 * 3600 * 1000;
    } catch (error) {
        return false;
    }
}

export default function PushPromptBanner({compact = false}) {
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        if (dismissedRecently()) return undefined;
        pushState().then((state) => {
            if (active) setVisible(state === "permission_required");
        });
        return () => { active = false; };
    }, []);

    if (!visible) return null;

    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch (error) {
            // per-device convenience only
        }
        setVisible(false);
    };

    const enable = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const key = await notificationApi.pushKey();
            await enablePush(key?.publicKey);
            dispatch(setMessage({severity: "success", content: "Push روی این دستگاه فعال شد."}));
            setVisible(false);
        } catch (error) {
            dispatch(setMessage({severity: "warn", content: ERRORS[error?.message] || "فعال‌سازی Push انجام نشد."}));
            if (error?.message === "push.blocked") setVisible(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`flex align-items-center gap-2 border-round-lg bg-primary-50 border-1 border-primary-100 ${compact ? "p-2 m-2" : "p-2 px-3 mb-2"}`} dir="rtl">
            <i className="pi pi-bell text-primary"/>
            <span className="text-xs text-700 flex-1 line-height-3">
                {compact ? "Push را روی این دستگاه فعال کنید." : "اعلان‌های مهم را روی این دستگاه هم دریافت کنید — حتی وقتی زوفینو بسته است."}
            </span>
            <Button label="فعال‌سازی" size="small" className="text-xs py-1 px-2" loading={busy} onClick={enable}/>
            <Button icon="pi pi-times" text rounded size="small" className="w-2rem h-2rem" aria-label="بعداً" onClick={dismiss}/>
        </div>
    );
}
